import { $, $$ } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { confirmAction, toastError, toastSuccess } from '../lib/toast.js';
import { readBoolean, readText } from '../lib/form.js';

/**
 * The Backups page.
 *
 * The schedule saves like any other setting. The three row actions do not:
 * taking, restoring and deleting are **acts**, each with its own request and
 * its own answer, and none of them belongs behind a save button that says
 * nothing about what it is going to do to a live server.
 */
interface RestoreReport {
  created: { roles: number; channels: number; emojis: number };
  updated: { roles: number; channels: number };
  skipped: { kind: string; name: string; reason: string }[];
  failed: { kind: string; name: string }[];
  lowered: string[];
  droppedOverwrites: number;
  orphaned: number;
}

export function initBackupsPage(): void {
  const found = $<HTMLFormElement>('[data-backups-form]');
  if (!found) return;

  const form: HTMLFormElement = found;
  const guildId = form.dataset.guildId ?? '';

  // Armed before anything else: saving is the essential function of the page,
  // and it must not depend on a widget that can throw.
  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      await api.put(`/api/backups/schedule?guildId=${guildId}`, {
        guildId,
        frequency: readText(form, 'frequency') || 'off',
      });
    },
  });

  const take = $<HTMLButtonElement>('[data-take-backup]', form);
  take?.addEventListener('click', () => {
    void (async () => {
      take.disabled = true;

      try {
        await api.post(`/api/backups?guildId=${guildId}`, {
          guildId,
          name: readText(form, 'name'),
        });

        // The list is rendered by the server, quota line included: reloading
        // shows the new backup exactly as the next visit will.
        location.reload();
      } catch (error) {
        toastError(error);
        take.disabled = false;
      }
    })();
  });

  for (const button of $$<HTMLButtonElement>('[data-restore]', form)) {
    button.addEventListener('click', () => void restore(button, form, guildId));
  }

  for (const button of $$<HTMLButtonElement>('[data-delete]', form)) {
    button.addEventListener('click', () => {
      void (async () => {
        const id = button.dataset.delete ?? '';
        const sure = await confirmAction(
          ct('backupDeleteConfirm', 'Delete this backup? It cannot be recovered.'),
        );
        if (!sure) return;

        try {
          await api.delete(`/api/backups/${id}?guildId=${guildId}`);
          location.reload();
        } catch (error) {
          toastError(error);
        }
      })();
    });
  }
}

async function restore(
  button: HTMLButtonElement,
  form: HTMLFormElement,
  guildId: string,
): Promise<void> {
  const id = button.dataset.restore ?? '';
  const mode = readText(form, 'mode') || 'missing';

  /*
   * The confirmation names the mode, not merely the backup.
   *
   * "Restore?" and "Restore, overwriting the roles and channels you still
   * have?" are two different questions, and the operator has to be asked the
   * one that matches the switch they left on.
   */
  const sure = await confirmAction(
    mode === 'full'
      ? ct(
          'backupRestoreFullConfirm',
          'Restore “{name}” and put the stored settings back onto what still exists? Nothing is ever deleted.',
          { name: button.dataset.backupName ?? '' },
        )
      : ct('backupRestoreConfirm', 'Restore what is missing from “{name}”?', {
          name: button.dataset.backupName ?? '',
        }),
  );

  if (!sure) return;

  button.disabled = true;

  try {
    const answer = await api.post<{ report: RestoreReport }>(
      `/api/backups/${id}/restore?guildId=${guildId}`,
      {
        guildId,
        roles: readBoolean(form, 'scopeRoles'),
        channels: readBoolean(form, 'scopeChannels'),
        emojis: readBoolean(form, 'scopeEmojis'),
        mode,
      },
    );

    const { created, updated, skipped, failed } = answer.report;

    // Figures rather than "done": a restore that put back three of the eleven
    // channels somebody expected is a thing they need to be told immediately.
    toastSuccess(
      ct(
        'backupRestoreReport',
        'Created: {created}. Restored: {updated}. Skipped: {skipped}. Refused: {failed}.',
        {
          created: String(created.roles + created.channels + created.emojis),
          updated: String(updated.roles + updated.channels),
          skipped: String(skipped.length),
          failed: String(failed.length),
        },
      ),
    );
  } catch (error) {
    toastError(error);
  } finally {
    button.disabled = false;
  }
}
